import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ChevronDownIcon } from 'lucide-react';
const faqs = [{
  question: 'What is KittyPot?',
  answer: 'KittyPot helps groups track shared expenses, split costs fairly and see the simplest way to settle up.'
}, {
  question: 'Do my friends need to download the app to join?',
  answer: 'Friends open a shared link and join a kitty right in their browser. Create a kitty, add expenses and see the simplest way to settle up together.'
}, {
  question: 'How does settling up work in KittyPot?',
  answer: 'KittyPot shows the clearest path to settle up. Everyone can settle directly with each other, however they like, and confirm when it is received.'
}, {
  question: 'Does KittyPot hold my money?',
  answer: "You're always in control of your money — you settle up directly with each other, your own way."
}, {
  question: 'What is a kitty?',
  answer: 'A kitty is a shared space for a group expense, trip, event or plan. Members add expenses and see the simplest way to settle up.'
}, {
  question: 'Can I split expenses unequally?',
  answer: 'Yes. KittyPot supports equal splits and custom amounts, so every share is clear.'
}, {
  question: 'What is “Simplest way to settle”?',
  answer: 'KittyPot works out the fewest payments needed to make everyone even in a kitty.'
}, {
  question: 'What is My Balance?',
  answer: 'My Balance brings together what you’re owed and what you owe across all your kitties.'
}, {
  question: 'What does “pending confirmation” mean?',
  answer: 'It shows that a payment has been marked as paid and is ready for the receiver to confirm.'
}, {
  question: 'Can I use KittyPot as a guest?',
  answer: 'Yes. Guest mode gives you a simple way to explore KittyPot on your device.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-478" data-mp="2h4379tnly782">
      <SEO title="FAQ | KittyPot — Shared Expense & Settlement Tracker" description="Answers about KittyPot: tracking shared expenses, splitting costs fairly, settling up your own way, and keeping group costs clear." ogTitle="KittyPot FAQ" ogDescription="Track shared expenses, split costs fairly, and see the simplest way to settle up." path="/faq" jsonLd={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    }} data-id="element-479" data-mp="2cdczk9rplsa7" />
      <Navbar data-id="element-480" data-mp="10ro48eyjuclu" />
      <main className="pt-36 pb-24" data-id="element-481" data-mp="10s689xrgdv4b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-482" data-mp="3ccwyu3kg0z5c">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-center mb-14" data-id="element-483" data-mp="3cfjg4ux4yt0r">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-484" data-mp="37mglskkjbfji">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-gray-500 max-w-lg mx-auto" data-id="element-485" data-mp="2tldzc6pmo382">
              Everything you need to know about KittyPot. Can't find what you're
              looking for?{' '}
              <a href="/contact" className="text-kitty-yellow-600 hover:text-kitty-yellow-700 font-medium underline underline-offset-2" data-id="element-486" data-mp="2jpsyibwap8zw">
                Get in touch
              </a>
              .
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="space-y-3" data-id="element-487" data-mp="hvbkcb209f6b">
            {faqs.map((faq, index) => <div key={index} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-488" data-mp="2e1ug34hcaeqf" data-mpi={index}>
                <button onClick={() => toggle(index)} className="w-full flex items-center justify-between px-6 py-5 text-left gap-4" data-id="element-489" data-mp="29xebr7ndjfdb">
                  <span className="text-base font-semibold text-kitty-dark leading-snug" data-id="element-490" data-mp="200rqihr4ymar">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: openIndex === index ? 180 : 0
              }} transition={{
                duration: 0.2
              }} className="flex-shrink-0" data-id="element-491" data-mp="1v99qbvwxspqu">
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-492" data-mp="1mao9c5745is6" />
                  </motion.div>
                </button>
                <AnimatePresence initial={false} data-id="element-493" data-mp="1hcd09won7cb0">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-494" data-mp="1ccida47zn6v8">
                      <div className="px-6 pb-5 pt-0" data-id="element-495" data-mp="5se8477xem2a">
                        <p className="text-gray-500 leading-relaxed text-[15px]" data-id="element-496" data-mp="358kv0mfrrw0u">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="text-center mt-16 bg-gray-950 rounded-2xl p-10" data-id="element-497" data-mp="3izpm3052nfzk">
            <h2 className="text-2xl font-bold text-white mb-3" data-id="element-498" data-mp="2qhc2a4gklujh">
              Still have questions?
            </h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto" data-id="element-499" data-mp="3trpuukowt405">
              We're happy to help. Reach out and we'll get back to you within 24
              hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-500" data-mp="7don8ek83lfu">
              <a href="/contact" className="bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-3 rounded-full font-bold transition-all active:scale-95 shadow-md hover:shadow-lg" data-id="element-501" data-mp="2g6kx70po4gt">
                Contact Us
              </a>
              <a href="mailto:support@kittypot.com" className="text-gray-400 hover:text-white font-medium transition-colors text-sm" data-id="element-502" data-mp="3jvmsbdq5b3co">
                or email support@kittypot.com
              </a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-503" data-mp="z3ufnui1sk4p" />
    </div>;
}